import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Computer,
  Filter, 
  Star, 
  Eye, 
  ShoppingCart, 
  Cpu, 
  CircuitBoard,
  HardDrive, 
  Monitor,
  Zap,
  MemoryStick,
  Fan
} from 'lucide-react';
import { PcConfigService, PcConfiguration, QueryParams } from '../services/pcConfigService';
import { useCart } from '../contexts/CartContext';
import Layout from '../components/layout/Layout';
import logger from '../utils/logger';
import { config as envConfig } from '../config/env';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

const PCConfigsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [configurations, setConfigurations] = useState<PcConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Search removed as per requirements
  const [sortOption, setSortOption] = useState(
    searchParams.get('sort') || 'price_asc'
  );
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'new' | 'used'>(searchParams.get('condition') as 'new' | 'used' || 'new');
  const [priceRange, setPriceRange] = useState({
    min: searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : 0,
    max: searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : 10000
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const { addToCart, cart } = useCart();

  // Page and filters already live in the URL, so back only needs the offset.
  useScrollRestoration({ ready: !loading });

  // Fetch configurations
  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      setError('');

      const params: QueryParams = {
        page: currentPage,
        limit: 12,
        condition: activeTab,
        sortBy: 'totalPrice',
        sortOrder: sortOption === 'price_desc' ? 'DESC' : 'ASC',
        minPrice: priceRange.min > 0 ? Number(priceRange.min) : undefined,
        maxPrice: priceRange.max < 10000 ? Number(priceRange.max) : undefined
      };

      // Debug logging
      logger.log('Fetching with params:', params);

      const response = await PcConfigService.getPublishedConfigs(params);
      setConfigurations(response.items);
      setTotalPages(response.pagination.pages);
      setTotalItems(response.pagination.total);
      
      // Update URL params
      const newParams = new URLSearchParams();
      if (activeTab !== 'new') newParams.set('condition', activeTab);
      if (sortOption !== 'price_asc') newParams.set('sort', sortOption);
      if (priceRange.min > 0) newParams.set('minPrice', priceRange.min.toString());
      if (priceRange.max < 10000) newParams.set('maxPrice', priceRange.max.toString());
      if (currentPage > 1) newParams.set('page', currentPage.toString());
      
      setSearchParams(newParams);
    } catch (err) {
      logger.error('Failed to fetch configurations:', err);
      setError('Грешка при зареждане на конфигурациите');
    } finally {
      setLoading(false);
    }
  };

  // Effect for immediate changes (page, sort, tab)
  useEffect(() => {
    fetchConfigurations();
  }, [currentPage, sortOption, activeTab]);

  // Effect for debounced price changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchConfigurations();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [priceRange.min, priceRange.max]);

  const handleAddToCart = async (config: PcConfiguration) => {
    // Add the complete configuration as a single cart item
    const finalPrice = config.manualPrice 
      ? parseFloat(config.manualPrice?.toString() || '0') || 0
      : parseFloat(config.totalPrice?.toString() || '0') || 0;

    // Get PC case image if available (use custom image for used configs)
    let imageUrl = null;

    // First check if there are custom images (for used configs) - use the first one
    if (config.imageUrls && config.imageUrls.length > 0) {
      // Construct full URL for custom uploaded images
      const baseUrl = envConfig.api.baseUrl;
      imageUrl = `${baseUrl}${config.imageUrls[0]}`;
    }
    // Fallback to old single image field for backward compatibility
    else if (config.imageUrl) {
      const baseUrl = envConfig.api.baseUrl;
      imageUrl = `${baseUrl}${config.imageUrl}`;
    }
    // Otherwise try to get PC case image
    else if (config.pcCaseId) {
      try {
        imageUrl = await PcConfigService.getPcCaseImageUrl(config.pcCaseId, config.pcCaseSupplier);
      } catch (error) {
        logger.error('Failed to fetch PC case image for cart:', error);
      }
    }
      
    const cartItem = {
      id: config.id,
      referenceNumber: `PC-CONFIG-${config.id}`,
      name: config.name,
      price: finalPrice,
      pricePromo: null,
      quantity: 1,
      image: imageUrl,
      // Without this the checkout resolves the build as a plain catalogue
      // product whose id happens to equal the configuration id.
      pcConfigId: config.id
    };

    addToCart(cartItem);
  };

  const getComponentIcon = (componentType: string) => {
    switch (componentType) {
      case 'cpu': return <Cpu className="h-4 w-4" />;
      case 'gpu': return <Monitor className="h-4 w-4" />;
      case 'ram': return <MemoryStick className="h-4 w-4" />;
      case 'storage': return <HardDrive className="h-4 w-4" />;
      case 'motherboard': return <CircuitBoard className="h-4 w-4" />;
      case 'cooler': return <Fan className="h-4 w-4" />;
      case 'power': return <Zap className="h-4 w-4" />;
      default: return <Computer className="h-4 w-4" />;
    }
  };

  const renderConfigurationCard = (config: PcConfiguration) => {
    const componentsList = [
      { name: config.cpuName, type: 'cpu' },
      { name: config.gpuName, type: 'gpu' },
      { name: config.ramName, type: 'ram' },
      { name: config.ssdName, type: 'storage' },
      { name: config.hddName, type: 'storage' },
      { name: config.motherboardName, type: 'motherboard' },
      { name: config.cpuCoolerName, type: 'cooler' },
      { name: config.powerSupplyName, type: 'power' },
      { name: config.pcCaseName, type: 'case' }
    ].filter((component): component is { name: string; type: string } => !!component.name);

    return (
      <ConfigurationCardWithImage key={config.id} config={config} componentsList={componentsList} onAddToCart={handleAddToCart} />
    );
  };

  // Component to handle image loading for configuration cards
  const ConfigurationCardWithImage: React.FC<{
    config: PcConfiguration;
    componentsList: Array<{name: string; type: string}>;
    onAddToCart: (config: PcConfiguration) => void;
  }> = ({ config, componentsList, onAddToCart }) => {
    const navigate = useNavigate();
    const [images, setImages] = useState<string[]>([]);
    const openDetails = () => navigate(`/konfiguratsii/${config.id}`);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageLoading, setImageLoading] = useState(false);

    useEffect(() => {
      const fetchPcCaseImages = async () => {
        const baseUrl = envConfig.api.baseUrl;

        // If there are custom images (for used configs), use them
        if (config.imageUrls && config.imageUrls.length > 0) {
          const fullUrls = config.imageUrls.map(url => `${baseUrl}${url}`);
          setImages(fullUrls);
          setImageLoading(false);
        }
        // Fallback to old single image field for backward compatibility
        else if (config.imageUrl) {
          setImages([`${baseUrl}${config.imageUrl}`]);
          setImageLoading(false);
        }
        // Otherwise fetch PC case images from the API
        else if (config.pcCaseId) {
          setImageLoading(true);
          try {
            const urls = await PcConfigService.getPcCaseImages(config.pcCaseId, config.pcCaseSupplier);
            setImages(urls);
          } catch (error) {
            logger.error('Failed to fetch PC case images:', error);
            setImages([]);
          } finally {
            setImageLoading(false);
          }
        } else {
          // No images available
          setImages([]);
          setImageLoading(false);
        }
      };

      fetchPcCaseImages();
    }, [config.pcCaseId, config.pcCaseSupplier, config.imageUrl, config.imageUrls]);

    return (
      <div
        role="link"
        tabIndex={0}
        onClick={openDetails}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openDetails();
          }
        }}
        className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        {/* Configuration Image */}
        {imageLoading && (
          <div className="relative h-40 sm:h-48 bg-gray-100 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}
        {!imageLoading && images.length > 0 && (
          <div className="relative h-40 sm:h-48 bg-gray-100 group">
            <img
              src={images[currentImageIndex]}
              alt={config.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.style.display = 'none';
              }}
            />
            {/* Navigation arrows for multiple images */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {/* Image dots indicator */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-3 sm:mb-4">
            <div className="flex-1">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 line-clamp-2">{config.name}</h3>
              {config.isFeatured && (
                <div className="flex items-center mt-1">
                  <Star className="h-4 w-4 text-yellow-500" fill="currentColor" />
                  <span className="ml-1 text-sm text-yellow-600 font-medium">Препоръчано</span>
                </div>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <Eye className="h-4 w-4 mr-1" />
              {config.viewCount}
            </div>
          </div>

          {/* Description */}
          {config.description && (
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-2">{config.description}</p>
          )}

          {/* Components List */}
          <div className="mb-3 sm:mb-4">
            <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Основни компоненти:</h4>
            <div className="space-y-1">
              {componentsList.slice(0, 3).map((component, index) => (
                <div key={index} className="flex items-center text-xs sm:text-sm text-gray-600">
                  {getComponentIcon(component.type)}
                  <span className="ml-2 truncate">{component.name}</span>
                </div>
              ))}
              {componentsList.length > 3 && (
                <div className="text-xs sm:text-sm text-gray-500">
                  +{componentsList.length - 3} други компонента
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {config.tags && config.tags.length > 0 && (
            <div className="mb-3 sm:mb-4">
              <div className="flex flex-wrap gap-1">
                {config.tags.slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-block px-2 py-1 text-xs bg-green-100 text-green-800 rounded"
                  >
                    {tag}
                  </span>
                ))}
                {config.tags.length > 2 && (
                  <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                    +{config.tags.length - 2}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Price and Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-3 sm:pt-4 border-t border-gray-200 gap-3">
            <div>
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-green-600">
                €{(config.manualPrice
                  ? parseFloat(config.manualPrice?.toString() || '0') || 0
                  : parseFloat(config.totalPrice?.toString() || '0') || 0).toFixed(2)}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">
                {config.condition === 'used'
                  ? (config.manualPrice ? 'Крайна цена' : 'Обща цена')
                  : (config.manualPrice ? 'Крайна цена с ДДС' : 'Обща цена с ДДС')}
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToCart(config);
                }}
                className="flex-1 sm:w-28 flex items-center justify-center px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm sm:text-base"
              >
                <ShoppingCart className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Купи</span>
                <span className="sm:hidden">Купи</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
      <div className="py-4">
        {/* Page Header */}
        <div className="text-center mb-6 px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">PC Конфигурации</h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Открийте готови PC конфигурации, подбрани специално за различни нужди -
            от офис работа до високопроизводителни gaming системи.
          </p>
        </div>

        {/* Tabs for New and Used */}
        <div className="flex justify-center mb-6 px-4">
          <div className="bg-white rounded-lg shadow-md p-1 inline-flex w-full sm:w-auto">
            <button
              onClick={() => {
                setActiveTab('new');
                setCurrentPage(1);
              }}
              className={`flex-1 sm:w-40 px-4 py-2 sm:py-3 rounded-md font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'new'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Нови
            </button>
            <button
              onClick={() => {
                setActiveTab('used');
                setCurrentPage(1);
              }}
              className={`flex-1 sm:w-40 px-4 py-2 sm:py-3 rounded-md font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'used'
                  ? 'bg-green-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Употребявани
            </button>
          </div>
        </div>

        {/* Filters and Sort */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 mx-4 sm:mx-0">
          <div className="flex flex-col gap-4">
            {/* Sort and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-center">
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="w-full sm:w-auto px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-xs sm:text-sm bg-white"
              >
                <option value="price_asc">Цена ↑</option>
                <option value="price_desc">Цена ↓</option>
              </select>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center px-3 sm:px-4 py-1.5 sm:py-2 border rounded-lg transition-colors text-xs sm:text-sm ${
                  showFilters
                    ? 'bg-green-50 border-green-300 text-green-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                Филтри
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Минимална цена
                    </label>
                    <span className="text-sm font-semibold text-green-600">
                      €{priceRange.min.toFixed(0)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="100"
                    value={priceRange.min}
                    onChange={(e) => {
                      const newMin = Number(e.target.value);
                      setPriceRange(prev => ({
                        ...prev,
                        min: newMin,
                        // Ensure min doesn't exceed max
                        max: prev.max < newMin ? newMin + 100 : prev.max
                      }));
                    }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Максимална цена
                    </label>
                    <span className="text-sm font-semibold text-green-600">
                      €{priceRange.max.toFixed(0)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="10000"
                    step="100"
                    value={priceRange.max}
                    onChange={(e) => {
                      const newMax = Number(e.target.value);
                      setPriceRange(prev => ({
                        ...prev,
                        max: newMax,
                        // Ensure max doesn't go below min
                        min: prev.min > newMax ? Math.max(0, newMax - 100) : prev.min
                      }));
                    }}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 mx-4 sm:mx-0 text-sm sm:text-base">
            {error}
          </div>
        )}

        {/* Results Header */}
        <div className="flex justify-between items-center mb-6 px-4 sm:px-0">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">
            Намерени конфигурации ({totalItems})
          </h2>
        </div>

        {/* Configurations Grid */}
        {loading ? (
          <div className="text-center py-12 px-4">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm sm:text-base">Зареждане на конфигурации...</p>
          </div>
        ) : configurations.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Computer className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-2">Няма намерени конфигурации</h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4">
              {priceRange.min > 0 || priceRange.max < 10000
                ? 'Няма конфигурации, които да отговарят на филтрите.'
                : 'Все още няма публикувани PC конфигурации.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 px-4 sm:px-0">
              {configurations.map(renderConfigurationCard)}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center px-4 sm:px-0">
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <span className="hidden sm:inline">Предишна</span>
                    <span className="sm:hidden">←</span>
                  </button>

                  {/* Mobile: Show fewer page numbers */}
                  <div className="hidden sm:flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => Math.abs(page - currentPage) <= 2 || page === 1 || page === totalPages)
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 py-2 text-gray-500">...</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 rounded-lg text-sm ${
                              currentPage === page
                                ? 'bg-primary-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                  </div>

                  {/* Mobile: Show current page info */}
                  <div className="flex sm:hidden items-center gap-1">
                    {currentPage > 1 && (
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                      >
                        1
                      </button>
                    )}
                    {currentPage > 2 && <span className="text-gray-500">...</span>}
                    <span className="px-3 py-1 bg-primary-600 text-white rounded text-sm">
                      {currentPage}
                    </span>
                    {currentPage < totalPages - 1 && <span className="text-gray-500">...</span>}
                    {currentPage < totalPages && (
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <span className="hidden sm:inline">Следваща</span>
                    <span className="sm:hidden">→</span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default PCConfigsPage;